import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Bug, Plus, ChevronRight, ChevronLeft, Check, Trash2, Edit, X, 
  AlertTriangle, Filter, TrendingUp, MapPin, RefreshCw, BarChart3, 
  Bell, Calendar, Wrench, Users, Target, History, Camera, Image, Eye,
  Timer, Droplets, FlaskConical, Settings
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import ProduccionLayout from '@/components/ProduccionLayout';
import PushNotificationSettings from '@/components/PushNotificationSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configuración de lotes
const LOTES_CONFIG = {
  "Agropal": { rango: [1, 4], color: "bg-blue-500" },
  "La Isla": { rango: [5, 13], color: "bg-green-500" },
  "Cenízaro": { rango: [14, 16], color: "bg-yellow-500" },
  "Galerón": { rango: [17, 18], color: "bg-orange-500" },
  "Laurel": { rango: [19, 22], color: "bg-purple-500" },
  "La Montaña": { rango: [23, 26], color: "bg-red-500" },
};

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

export default function RegistroPicudos({ user, onLogout }) {
  // Estados principales
  const [registros, setRegistros] = useState([]);
  const [trampas, setTrampas] = useState([]);
  const [trabajadores, setTrabajadores] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Estados para ciclos de mantenimiento
  const [ciclosMantenimiento, setCiclosMantenimiento] = useState(null);
  
  // Estados de filtros
  const [añoFiltro, setAñoFiltro] = useState(new Date().getFullYear());
  const [mesFiltro, setMesFiltro] = useState(new Date().getMonth() + 1);
  const [loteFiltro, setLoteFiltro] = useState('todos');
  
  // Estados del formulario
  const [showForm, setShowForm] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    fecha: new Date().toISOString().split('T')[0],
    lote: '',
    encargado_id: '',
    encargado_nombre: '',
  });
  const [registrosForm, setRegistrosForm] = useState([]);
  
  // Estados de tabs
  const [activeTab, setActiveTab] = useState('registros');
  const [activeLoteTab, setActiveLoteTab] = useState('Agropal');
  
  // Estados para estadísticas
  const [añosComparar, setAñosComparar] = useState([2026]);
  const [datosGraficos, setDatosGraficos] = useState({});
  
  // Estados para historial de alertas
  const [todasAlertas, setTodasAlertas] = useState([]);
  const [loadingAlertas, setLoadingAlertas] = useState(false);
  
  // Estados para edición e historial de cambios
  const [showEditModal, setShowEditModal] = useState(false);
  const [showHistorialModal, setShowHistorialModal] = useState(false);
  const [registroEditar, setRegistroEditar] = useState(null);
  const [historialData, setHistorialData] = useState(null);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [registroEliminar, setRegistroEliminar] = useState(null);
  const [deletingRegistro, setDeletingRegistro] = useState(false);
  
  // Estados para imagen de evidencia
  const [uploadingImage, setUploadingImage] = useState({});
  const [showImageModal, setShowImageModal] = useState(false);
  const [imagenSeleccionada, setImagenSeleccionada] = useState(null);
  
  // Verificar si es super admin
  const isSuperAdmin = user?.is_admin || user?.role === 'super_admin' || user?.role === 'admin';

  // Cargar datos iniciales
  useEffect(() => {
    fetchData();
  }, [añoFiltro, mesFiltro, loteFiltro]);

  // Cargar todas las alertas cuando se cambia a la pestaña de alertas
  useEffect(() => {
    if (activeTab === 'alertas') {
      fetchTodasAlertas();
    }
  }, [activeTab]);

  const fetchTodasAlertas = async () => {
    try {
      setLoadingAlertas(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/alertas-picudos`, { headers });
      setTodasAlertas(response.data);
    } catch (error) {
      console.error('Error al cargar alertas:', error);
      toast.error('Error al cargar el historial de alertas');
    } finally {
      setLoadingAlertas(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Cargar datos en paralelo
      const [trampasRes, registrosRes, trabajadoresRes, estadisticasRes, alertasRes, ciclosRes] = await Promise.all([
        axios.get(`${API}/trampas`, { headers }),
        axios.get(`${API}/registros-picudos?año=${añoFiltro}&mes=${mesFiltro}${loteFiltro !== 'todos' ? `&lote=${loteFiltro}` : ''}`, { headers }),
        axios.get(`${API}/trabajadores`, { headers }),
        axios.get(`${API}/registros-picudos/estadisticas/resumen?año=${añoFiltro}&mes=${mesFiltro}${loteFiltro !== 'todos' ? `&lote=${loteFiltro}` : ''}`, { headers }),
        axios.get(`${API}/alertas-picudos?solo_no_leidas=true`, { headers }),
        axios.get(`${API}/trampas/ciclos-mantenimiento`, { headers }),
      ]);

      setTrampas(trampasRes.data);
      setRegistros(registrosRes.data);
      setTrabajadores(trabajadoresRes.data);
      setEstadisticas(estadisticasRes.data);
      setAlertas(alertasRes.data);
      setCiclosMantenimiento(ciclosRes.data);

      // Si no hay trampas, inicializarlas
      if (trampasRes.data.length === 0) {
        await axios.post(`${API}/trampas/inicializar`, {}, { headers });
        const newTrampas = await axios.get(`${API}/trampas`, { headers });
        setTrampas(newTrampas.data);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos para gráficos
  const fetchDatosGraficos = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(
        `${API}/registros-picudos/estadisticas/mensual?años=${añosComparar.join(',')}`,
        { headers }
      );
      setDatosGraficos(response.data);
    } catch (error) {
      console.error('Error al cargar datos de gráficos:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'estadisticas') {
      fetchDatosGraficos();
    }
  }, [activeTab, añosComparar]);

  // Obtener trampas filtradas por lote
  const trampasPorLote = useMemo(() => {
    const result = {};
    Object.keys(LOTES_CONFIG).forEach(lote => {
      const [inicio, fin] = LOTES_CONFIG[lote].rango;
      result[lote] = trampas.filter(t => t.numero >= inicio && t.numero <= fin);
    });
    return result;
  }, [trampas]);

  // Obtener registros agrupados por trampa
  const registrosPorTrampa = useMemo(() => {
    const result = {};
    registros.forEach(r => {
      if (!result[r.numero_trampa]) {
        result[r.numero_trampa] = [];
      }
      result[r.numero_trampa].push(r);
    });
    return result;
  }, [registros]);

  // Iniciar formulario de nuevo registro
  const handleNuevoRegistro = () => {
    setFormData({
      fecha: new Date().toISOString().split('T')[0],
      lote: '',
      encargado_id: '',
      encargado_nombre: '',
    });
    setRegistrosForm([]);
    setCurrentStep(1);
    setShowForm(true);
  };

  // Abrir modal de edición
  const handleEdit = (registro) => {
    setRegistroEditar({ ...registro });
    setShowEditModal(true);
  };

  // Guardar edición
  const handleSaveEdit = async () => {
    if (!registroEditar) return;
    
    try {
      setSavingEdit(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.put(`${API}/registros-picudos/${registroEditar.id}`, {
        ...registroEditar,
        updated_by: user?.full_name || user?.username
      }, { headers });
      
      toast.success('Registro actualizado correctamente');
      setShowEditModal(false);
      setRegistroEditar(null);
      fetchData();
    } catch (error) {
      console.error('Error al actualizar:', error);
      toast.error('Error al actualizar el registro');
    } finally {
      setSavingEdit(false);
    }
  };

  // Confirmar eliminación
  const handleConfirmDelete = (registro) => {
    setRegistroEliminar(registro);
    setShowDeleteConfirm(true);
  };

  // Eliminar registro
  const handleDelete = async () => {
    if (!registroEliminar) return;
    
    try {
      setDeletingRegistro(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.delete(`${API}/registros-picudos/${registroEliminar.id}`, { headers });
      
      toast.success(`Registro de trampa #${registroEliminar.numero_trampa} eliminado`);
      setShowDeleteConfirm(false);
      setRegistroEliminar(null);
      fetchData();
    } catch (error) {
      console.error('Error al eliminar:', error);
      toast.error('Error al eliminar el registro');
    } finally {
      setDeletingRegistro(false);
    }
  };

  // Ver historial de cambios
  const handleViewHistorial = async (registro) => {
    try {
      setLoadingHistorial(true);
      setShowHistorialModal(true);
      
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/registros-picudos/${registro.id}/historial`, { headers });
      setHistorialData(response.data);
    } catch (error) {
      console.error('Error al cargar historial:', error);
      toast.error('Error al cargar el historial');
    } finally {
      setLoadingHistorial(false);
    }
  };

  // Subir imagen de evidencia
  const handleUploadImage = async (index, file) => {
    if (!file) return;
    
    try {
      setUploadingImage({ ...uploadingImage, [index]: true });
      
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('user_id', user?.id || 'system');
      
      const response = await axios.post(`${API}/registros-picudos/upload-imagen`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        const newRegistros = [...registrosForm];
        newRegistros[index] = { 
          ...newRegistros[index], 
          imagen_evidencia: response.data.file_path 
        };
        setRegistrosForm(newRegistros);
        toast.success('Imagen subida correctamente');
      }
    } catch (error) {
      console.error('Error al subir imagen:', error);
      if (error.response?.data?.detail?.includes('Google Drive no está conectado')) {
        toast.error('Conecta Google Drive en Configuración para subir imágenes');
      } else {
        toast.error('Error al subir la imagen');
      }
    } finally {
      setUploadingImage({ ...uploadingImage, [index]: false });
    }
  };

  // Ver imagen ampliada
  const handleViewImage = (imagenUrl) => {
    setImagenSeleccionada(imagenUrl);
    setShowImageModal(true);
  };

  // Cuando se selecciona un lote, preparar las trampas
  const handleLoteChange = (lote) => {
    setFormData({ ...formData, lote });
    const trampasDeLote = trampasPorLote[lote] || [];
    setRegistrosForm(trampasDeLote.map(t => ({
      trampa_id: t.id,
      numero_trampa: t.numero,
      hembras: 0,
      machos: 0,
      sin_clasificar: 0,
      cambio_miel_caldo: false,
      cambio_feromonas: false,
      cambio_galon: false,
      cambio_tapa: false,
      cambio_alambres: false,
      cambio_stickers: false,
      detalle: '',
      imagen_evidencia: null,
    })));
  };

  // Actualizar registro individual en el formulario
  const updateRegistroForm = (index, field, value) => {
    const newRegistros = [...registrosForm];
    newRegistros[index] = { ...newRegistros[index], [field]: value };
    setRegistrosForm(newRegistros);
  };

  // Guardar registros
  const handleGuardar = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const payload = {
        fecha: formData.fecha,
        lote: formData.lote,
        encargado_id: formData.encargado_id,
        encargado_nombre: formData.encargado_nombre,
        usuario_registro: user?.full_name || user?.username,
        registros: registrosForm
      };

      await axios.post(`${API}/registros-picudos/masivo`, payload, { headers });
      
      toast.success(`Se guardaron ${registrosForm.length} registros correctamente`);
      setShowForm(false);
      fetchData();
    } catch (error) {
      console.error('Error al guardar:', error);
      toast.error('Error al guardar los registros');
    }
  };

  // Marcar alerta como leída
  const handleMarcarAlertaLeida = async (alertaId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/alertas-picudos/${alertaId}/leer`, {}, { headers });
      setAlertas(alertas.filter(a => a.id !== alertaId));
      toast.success('Alerta marcada como leída');
    } catch (error) {
      toast.error('Error al marcar alerta');
    }
  };

  // Datos para gráfico de barras por lote
  const datosBarrasLote = useMemo(() => {
    if (!estadisticas?.por_lote) return [];
    return Object.entries(estadisticas.por_lote).map(([lote, data]) => ({
      lote,
      picudos: data.total_picudos,
      ppt: data.ppt,
    }));
  }, [estadisticas]);

  // Preparar datos para gráfico de líneas mensual
  const datosLineasMensual = useMemo(() => {
    if (!datosGraficos || Object.keys(datosGraficos).length === 0) return [];
    
    const meses = MESES.map((nombre, idx) => ({ mes: idx + 1, mes_nombre: nombre.substring(0, 3) }));
    
    return meses.map(m => {
      const punto = { mes: m.mes_nombre };
      añosComparar.forEach(año => {
        const datos = datosGraficos[año]?.find(d => d.mes === m.mes);
        punto[`ppt_${año}`] = datos?.ppt || 0;
        punto[`total_${año}`] = datos?.total_picudos || 0;
      });
      return punto;
    });
  }, [datosGraficos, añosComparar]);

  const coloresLineas = ['#10b981', '#3b82f6', '#f59e0b'];

  if (loading) {
    return (
      <ProduccionLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </ProduccionLayout>
    );
  }

  return (
    <ProduccionLayout user={user} onLogout={onLogout}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Bug className="w-7 h-7 text-amber-600" />
              Registro de Picudos y Trampas
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Monitoreo y control de plagas en trampas
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </Button>
            <Button onClick={handleNuevoRegistro} className="gap-2 bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4" />
              Nuevo Registro
            </Button>
          </div>
        </div>

        {/* Alertas activas */}
        {alertas.length > 0 && (
          <div className="mb-6">
            <Card className="border-red-200 bg-red-50 dark:bg-red-900/20">
              <CardContent className="py-3">
                <div className="flex items-center gap-2 mb-2">
                  <Bell className="w-5 h-5 text-red-600" />
                  <span className="font-semibold text-red-800 dark:text-red-200">
                    {alertas.length} Alerta{alertas.length > 1 ? 's' : ''} Activa{alertas.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="space-y-1">
                  {alertas.slice(0, 3).map(alerta => (
                    <div key={alerta.id} className="flex items-center justify-between text-sm">
                      <span className="text-red-700 dark:text-red-300">{alerta.mensaje}</span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleMarcarAlertaLeida(alerta.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filtros */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Select value={String(añoFiltro)} onValueChange={(v) => setAñoFiltro(Number(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Año" />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map(año => (
                <SelectItem key={año} value={String(año)}>{año}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={String(mesFiltro)} onValueChange={(v) => setMesFiltro(Number(v))}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Mes" />
            </SelectTrigger>
            <SelectContent>
              {MESES.map((mes, idx) => (
                <SelectItem key={idx} value={String(idx + 1)}>{mes}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={loteFiltro} onValueChange={setLoteFiltro}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Lote" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los lotes</SelectItem>
              {Object.keys(LOTES_CONFIG).map(lote => (
                <SelectItem key={lote} value={lote}>{lote}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-300">PPT</p>
                  <p className="text-2xl font-bold text-amber-900 dark:text-amber-100">
                    {estadisticas?.ppt?.toFixed(1) || '0.0'}
                  </p>
                </div>
                <Target className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-red-700 dark:text-red-300">Alertas</p>
                  <p className="text-2xl font-bold text-red-900 dark:text-red-100">
                    {estadisticas?.alertas_activas || 0}
                  </p>
                </div>
                <Bell className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300">Total Picudos</p>
                  <p className="text-2xl font-bold text-emerald-900 dark:text-emerald-100">
                    {estadisticas?.total_picudos || 0}
                  </p>
                </div>
                <Bug className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-blue-700 dark:text-blue-300">Registros</p>
                  <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                    {estadisticas?.total_registros || 0}
                  </p>
                </div>
                <Calendar className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-purple-700 dark:text-purple-300">Trampas</p>
                  <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                    {estadisticas?.trampas_revisadas || 0}
                  </p>
                </div>
                <MapPin className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-orange-700 dark:text-orange-300">Mantenim.</p>
                  <p className="text-2xl font-bold text-orange-900 dark:text-orange-100">
                    {estadisticas?.mantenimientos ? 
                      Object.values(estadisticas.mantenimientos).reduce((a, b) => a + b, 0) : 0}
                  </p>
                </div>
                <Wrench className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Timer de Ciclos de Mantenimiento */}
        {ciclosMantenimiento && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Timer Feromonas */}
            <div className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
              ciclosMantenimiento.feromonas?.en_retraso 
                ? 'bg-red-50 border-red-300 dark:bg-red-900/20 dark:border-red-700' 
                : 'bg-purple-50 border-purple-200 dark:bg-purple-900/20 dark:border-purple-700'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${
                  ciclosMantenimiento.feromonas?.en_retraso 
                    ? 'bg-red-200 dark:bg-red-800' 
                    : 'bg-purple-200 dark:bg-purple-800'
                }`}>
                  <FlaskConical className={`w-5 h-5 ${
                    ciclosMantenimiento.feromonas?.en_retraso 
                      ? 'text-red-700 dark:text-red-300' 
                      : 'text-purple-700 dark:text-purple-300'
                  }`} />
                </div>
                <div>
                  <p className={`text-sm font-medium ${
                    ciclosMantenimiento.feromonas?.en_retraso 
                      ? 'text-red-800 dark:text-red-200' 
                      : 'text-purple-800 dark:text-purple-200'
                  }`}>
                    Feromonas
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Ciclo: {ciclosMantenimiento.feromonas?.ciclo_dias} días
                  </p>
                </div>
              </div>
              
              <div className="text-right">
                <div className={`text-2xl font-bold ${
                  ciclosMantenimiento.feromonas?.en_retraso 
                    ? 'text-red-600 dark:text-red-400' 
                    : ciclosMantenimiento.feromonas?.dias_restantes <= 7 
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-purple-600 dark:text-purple-400'
                }`}>
                  {ciclosMantenimiento.feromonas?.dias_restantes === -999 
                    ? 'Sin datos' 
                    : ciclosMantenimiento.feromonas?.en_retraso 
                      ? `${Math.abs(ciclosMantenimiento.feromonas.dias_restantes)} días atraso`
                      : `${ciclosMantenimiento.feromonas.dias_restantes} días`
                  }
                </div>
                {ciclosMantenimiento.feromonas?.proximo_cambio && (
                  <p className={`text-xs ${
                    ciclosMantenimiento.feromonas?.en_retraso 
                      ? 'text-red-500' 
                      : 'text-gray-500'
                  }`}>
                    {ciclosMantenimiento.feromonas.en_retraso ? 'Vencido: ' : 'Próximo: '}
                    {new Date(ciclosMantenimiento.feromonas.proximo_cambio + 'T00:00:00').toLocaleDateString('es', { day: '2-digit', month: 'short' })}
                  </p>
                )}
              </div>
            </div>

            {/* Timer Caldos */}
            <div className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
              ciclosMantenimiento.caldos?.en_retraso 
                ? 'bg-red-50 border-red-300 dark:bg-red-900/20 dark:border-red-700' 
                : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${
                  ciclosMantenimiento.caldos?.en_retraso 
                    ? 'bg-red-200 dark:bg-red-800' 
                    : 'bg-amber-200 dark:bg-amber-800'
                }`}>
                  <Droplets className={`w-5 h-5 ${
                    ciclosMantenimiento.caldos?.en_retraso 
                      ? 'text-red-700 dark:text-red-300' 
                      : 'text-amber-700 dark:text-amber-300'
                  }`} />
                </div>
                <div>
                  <p className={`text-sm font-medium ${
                    ciclosMantenimiento.caldos?.en_retraso 
                      ? 'text-red-800 dark:text-red-200' 
                      : 'text-amber-800 dark:text-amber-200'
                  }`}>
                    Miel / Caldo
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Ciclo: {ciclosMantenimiento.caldos?.ciclo_dias} días
                  </p>
                </div>
              </div>
              
              <div className="text-right">
                <div className={`text-2xl font-bold ${
                  ciclosMantenimiento.caldos?.en_retraso 
                    ? 'text-red-600 dark:text-red-400' 
                    : ciclosMantenimiento.caldos?.dias_restantes <= 5 
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-emerald-600 dark:text-emerald-400'
                }`}>
                  {ciclosMantenimiento.caldos?.dias_restantes === -999 
                    ? 'Sin datos' 
                    : ciclosMantenimiento.caldos?.en_retraso 
                      ? `${Math.abs(ciclosMantenimiento.caldos.dias_restantes)} días atraso`
                      : `${ciclosMantenimiento.caldos.dias_restantes} días`
                  }
                </div>
                {ciclosMantenimiento.caldos?.proximo_cambio && (
                  <p className={`text-xs ${
                    ciclosMantenimiento.caldos?.en_retraso 
                      ? 'text-red-500' 
                      : 'text-gray-500'
                  }`}>
                    {ciclosMantenimiento.caldos.en_retraso ? 'Vencido: ' : 'Próximo: '}
                    {new Date(ciclosMantenimiento.caldos.proximo_cambio + 'T00:00:00').toLocaleDateString('es', { day: '2-digit', month: 'short' })}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tabs principales */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-4 lg:w-[650px]">
            <TabsTrigger value="registros" className="gap-2">
              <Bug className="w-4 h-4" />
              Registros
            </TabsTrigger>
            <TabsTrigger value="alertas" className="gap-2">
              <Bell className="w-4 h-4" />
              Alertas
              {alertas.length > 0 && (
                <Badge variant="destructive" className="ml-1 text-xs px-1.5">
                  {alertas.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="estadisticas" className="gap-2">
              <BarChart3 className="w-4 h-4" />
              Estadísticas
            </TabsTrigger>
            {isSuperAdmin && (
              <TabsTrigger value="configuracion" className="gap-2" data-testid="tab-configuracion">
                <Settings className="w-4 h-4" />
                Config
              </TabsTrigger>
            )}
          </TabsList>

          {/* Tab de Registros */}
          <TabsContent value="registros" className="space-y-4">
            {/* Sub-tabs por lote */}
            <Tabs value={activeLoteTab} onValueChange={setActiveLoteTab}>
              <TabsList className="flex flex-wrap h-auto gap-1">
                {Object.keys(LOTES_CONFIG).map(lote => {
                  const registrosLote = registros.filter(r => r.lote === lote);
                  return (
                    <TabsTrigger 
                      key={lote} 
                      value={lote}
                      className="text-xs sm:text-sm"
                    >
                      {lote}
                      {registrosLote.length > 0 && (
                        <Badge variant="secondary" className="ml-1 text-xs">
                          {registrosLote.length}
                        </Badge>
                      )}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {Object.keys(LOTES_CONFIG).map(lote => (
                <TabsContent key={lote} value={lote}>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center justify-between">
                        <span>{lote} (Trampas {LOTES_CONFIG[lote].rango[0]} - {LOTES_CONFIG[lote].rango[1]})</span>
                        <Badge variant={
                          (estadisticas?.por_lote?.[lote]?.ppt || 0) > 5 ? "destructive" : "secondary"
                        }>
                          PPT: {estadisticas?.por_lote?.[lote]?.ppt?.toFixed(1) || '0.0'}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-16">#Trampa</TableHead>
                              <TableHead>Fecha</TableHead>
                              <TableHead className="text-center">H</TableHead>
                              <TableHead className="text-center">M</TableHead>
                              <TableHead className="text-center">SC</TableHead>
                              <TableHead className="text-center">Total</TableHead>
                              <TableHead>Mantenimiento</TableHead>
                              <TableHead>Encargado</TableHead>
                              <TableHead className="text-center">Foto</TableHead>
                              <TableHead className="text-center">Acciones</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {registros
                              .filter(r => r.lote === lote)
                              .sort((a, b) => a.numero_trampa - b.numero_trampa)
                              .map(registro => (
                                <TableRow key={registro.id}>
                                  <TableCell className="font-medium">#{registro.numero_trampa}</TableCell>
                                  <TableCell>{registro.fecha}</TableCell>
                                  <TableCell className="text-center text-pink-600">{registro.hembras}</TableCell>
                                  <TableCell className="text-center text-blue-600">{registro.machos}</TableCell>
                                  <TableCell className="text-center text-gray-600">{registro.sin_clasificar}</TableCell>
                                  <TableCell className={`text-center font-bold ${
                                    registro.total_picudos > 5 ? 'text-red-600' : 'text-emerald-600'
                                  }`}>
                                    {registro.total_picudos}
                                    {registro.total_picudos > 5 && (
                                      <AlertTriangle className="w-4 h-4 inline ml-1 text-red-500" />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex gap-1 flex-wrap">
                                      {registro.cambio_miel_caldo && <Badge variant="outline" className="text-xs">Miel</Badge>}
                                      {registro.cambio_feromonas && <Badge variant="outline" className="text-xs">Fero</Badge>}
                                      {registro.cambio_galon && <Badge variant="outline" className="text-xs">Galón</Badge>}
                                      {registro.cambio_tapa && <Badge variant="outline" className="text-xs">Tapa</Badge>}
                                      {registro.cambio_alambres && <Badge variant="outline" className="text-xs">Alamb</Badge>}
                                      {registro.cambio_stickers && <Badge variant="outline" className="text-xs">Stick</Badge>}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-sm">{registro.encargado_nombre}</TableCell>
                                  <TableCell className="text-center">
                                    {registro.imagen_evidencia ? (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleViewImage(registro.imagen_evidencia)}
                                        className="text-blue-600 hover:text-blue-800"
                                      >
                                        <Image className="w-4 h-4" />
                                      </Button>
                                    ) : (
                                      <span className="text-gray-400 text-xs">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex gap-1 justify-center">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleEdit(registro)}
                                        className="text-amber-600 hover:text-amber-800"
                                        title="Editar registro"
                                      >
                                        <Edit className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleConfirmDelete(registro)}
                                        className="text-red-600 hover:text-red-800"
                                        title="Eliminar registro"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleViewHistorial(registro)}
                                        className="text-blue-600 hover:text-blue-800"
                                        title="Ver historial de cambios"
                                      >
                                        <History className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            {registros.filter(r => r.lote === lote).length === 0 && (
                              <TableRow>
                                <TableCell colSpan={10} className="text-center text-gray-500 py-8">
                                  No hay registros para este lote en el período seleccionado
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              ))}
            </Tabs>
          </TabsContent>

          {/* Tab de Alertas */}
          <TabsContent value="alertas" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Bell className="w-5 h-5 text-red-600" />
                  Historial de Alertas
                </CardTitle>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={fetchTodasAlertas}
                    className="gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Actualizar
                  </Button>
                  {todasAlertas.filter(a => !a.leida).length > 0 && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const headers = { Authorization: `Bearer ${token}` };
                          await axios.put(`${API}/alertas-picudos/leer-todas`, {}, { headers });
                          toast.success('Todas las alertas marcadas como leídas');
                          fetchTodasAlertas();
                          fetchData();
                        } catch (error) {
                          toast.error('Error al marcar alertas');
                        }
                      }}
                      className="gap-2"
                    >
                      <Check className="w-4 h-4" />
                      Marcar todas leídas
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {loadingAlertas ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                  </div>
                ) : todasAlertas.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No hay alertas registradas</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">Estado</TableHead>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Lote</TableHead>
                          <TableHead className="text-center">#Trampa</TableHead>
                          <TableHead className="text-center">Valor</TableHead>
                          <TableHead>Mensaje</TableHead>
                          <TableHead className="w-20">Acción</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {todasAlertas.map(alerta => (
                          <TableRow 
                            key={alerta.id}
                            className={alerta.leida ? 'opacity-60' : 'bg-red-50 dark:bg-red-900/10'}
                          >
                            <TableCell>
                              {alerta.leida ? (
                                <Badge variant="outline" className="text-xs">Leída</Badge>
                              ) : (
                                <Badge variant="destructive" className="text-xs">Nueva</Badge>
                              )}
                            </TableCell>
                            <TableCell className="font-medium">{alerta.fecha}</TableCell>
                            <TableCell>
                              <Badge 
                                variant={alerta.tipo === 'ppt_alto' ? 'destructive' : 'secondary'}
                                className="text-xs"
                              >
                                {alerta.tipo === 'ppt_alto' && '⚠️ PPT Alto'}
                                {alerta.tipo === 'feromonas_vencidas' && '🧪 Feromonas'}
                                {alerta.tipo === 'miel_vencida' && '🍯 Miel'}
                                {alerta.tipo === 'sin_registro' && '📋 Sin Registro'}
                              </Badge>
                            </TableCell>
                            <TableCell>{alerta.lote}</TableCell>
                            <TableCell className="text-center font-bold">#{alerta.trampa_numero}</TableCell>
                            <TableCell className="text-center">
                              {alerta.valor !== null && (
                                <span className={alerta.valor > (alerta.umbral || 5) ? 'text-red-600 font-bold' : ''}>
                                  {alerta.valor}
                                  {alerta.umbral && <span className="text-gray-400 text-xs ml-1">(umbral: {alerta.umbral})</span>}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="max-w-xs truncate text-sm">{alerta.mensaje}</TableCell>
                            <TableCell>
                              {!alerta.leida && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      const token = localStorage.getItem('token');
                                      const headers = { Authorization: `Bearer ${token}` };
                                      await axios.put(`${API}/alertas-picudos/${alerta.id}/leer`, {}, { headers });
                                      toast.success('Alerta marcada como leída');
                                      fetchTodasAlertas();
                                      fetchData();
                                    } catch (error) {
                                      toast.error('Error al marcar alerta');
                                    }
                                  }}
                                  className="text-emerald-600 hover:text-emerald-800"
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Resumen de alertas por tipo */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-red-200">
                <CardContent className="p-4 text-center">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-red-500" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">PPT Alto</p>
                  <p className="text-2xl font-bold text-red-600">
                    {todasAlertas.filter(a => a.tipo === 'ppt_alto').length}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-amber-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl mb-2">🧪</div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Feromonas</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {todasAlertas.filter(a => a.tipo === 'feromonas_vencidas').length}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-yellow-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl mb-2">🍯</div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Miel/Caldo</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {todasAlertas.filter(a => a.tipo === 'miel_vencida').length}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-blue-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl mb-2">📋</div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Sin Registro</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {todasAlertas.filter(a => a.tipo === 'sin_registro').length}
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tab de Estadísticas */}
          <TabsContent value="estadisticas" className="space-y-6">
            {/* Selector de años */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Comparar años</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {[2024, 2025, 2026].map(año => (
                    <Button
                      key={año}
                      variant={añosComparar.includes(año) ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        if (añosComparar.includes(año)) {
                          if (añosComparar.length > 1) {
                            setAñosComparar(añosComparar.filter(a => a !== año));
                          }
                        } else if (añosComparar.length < 3) {
                          setAñosComparar([...añosComparar, año].sort());
                        }
                      }}
                    >
                      {año}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Gráfico de barras por lote */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Picudos por Lote ({MESES[mesFiltro - 1]} {añoFiltro})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={datosBarrasLote}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="lote" tick={{ fontSize: 12 }} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="picudos" name="Total Picudos" fill="#10b981" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Gráfico de líneas mensual */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Tendencia PPT Mensual
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={datosLineasMensual}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="mes" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      {añosComparar.map((año, idx) => (
                        <Line
                          key={año}
                          type="monotone"
                          dataKey={`ppt_${año}`}
                          name={`PPT ${año}`}
                          stroke={coloresLineas[idx]}
                          strokeWidth={2}
                          dot={{ r: 4 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Distribución H/M/SC */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Distribución por Tipo</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-pink-50 dark:bg-pink-900/20 rounded-lg">
                    <p className="text-sm text-pink-700 dark:text-pink-300">Hembras (H)</p>
                    <p className="text-3xl font-bold text-pink-600">{estadisticas?.total_hembras || 0}</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-sm text-blue-700 dark:text-blue-300">Machos (M)</p>
                    <p className="text-3xl font-bold text-blue-600">{estadisticas?.total_machos || 0}</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-sm text-gray-700 dark:text-gray-300">Sin Clasificar (SC)</p>
                    <p className="text-3xl font-bold text-gray-600">{estadisticas?.total_sin_clasificar || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab de Configuración (Solo Admins) */}
          {isSuperAdmin && (
            <TabsContent value="configuracion" className="space-y-6" data-testid="tab-content-configuracion">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Notificaciones Push */}
                <PushNotificationSettings user={user} />
                
                {/* Información de ciclos */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Wrench className="w-5 h-5 text-orange-600" />
                      Ciclos de Mantenimiento
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FlaskConical className="w-4 h-4 text-purple-600" />
                          <span className="font-medium text-purple-800 dark:text-purple-200">Feromonas</span>
                        </div>
                        <Badge variant="outline" className="text-purple-700">75 días</Badge>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Droplets className="w-4 h-4 text-amber-600" />
                          <span className="font-medium text-amber-800 dark:text-amber-200">Miel / Caldo</span>
                        </div>
                        <Badge variant="outline" className="text-amber-700">22 días</Badge>
                      </div>
                    </div>
                    
                    <div className="pt-2 border-t">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Los ciclos se calculan automáticamente basándose en los registros de mantenimiento.
                        Cuando el 80% o más de las trampas registren un cambio en la misma fecha, 
                        se actualizará el ciclo correspondiente.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}
        </Tabs>

        {/* Modal de nuevo registro */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bug className="w-5 h-5 text-amber-600" />
                Nuevo Registro de Trampas - Paso {currentStep} de 2
              </DialogTitle>
            </DialogHeader>

            {/* Paso 1: Selección general */}
            {currentStep === 1 && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Fecha del Registro *</Label>
                    <Input
                      type="date"
                      value={formData.fecha}
                      onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Lote *</Label>
                    <Select value={formData.lote} onValueChange={handleLoteChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione un lote" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(LOTES_CONFIG).map(([lote, config]) => (
                          <SelectItem key={lote} value={lote}>
                            {lote} (Trampas {config.rango[0]} - {config.rango[1]})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label>Encargado *</Label>
                    <Select 
                      value={formData.encargado_id} 
                      onValueChange={(id) => {
                        const trabajador = trabajadores.find(t => t.id === id);
                        setFormData({ 
                          ...formData, 
                          encargado_id: id,
                          encargado_nombre: trabajador?.nombre_completo || ''
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione el encargado" />
                      </SelectTrigger>
                      <SelectContent>
                        {trabajadores.map(t => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.nombre_completo}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Paso 2: Registro por trampa */}
            {currentStep === 2 && (
              <div className="space-y-4 py-4">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Registrando {formData.lote} - {registrosForm.length} trampas
                </div>

                {/* Tabla de conteo */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 px-4 py-2 font-semibold text-emerald-800 dark:text-emerald-200">
                    Conteo de Picudos
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-20">#Trampa</TableHead>
                          <TableHead className="w-24">H (Hembras)</TableHead>
                          <TableHead className="w-24">M (Machos)</TableHead>
                          <TableHead className="w-24">SC</TableHead>
                          <TableHead className="w-20">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {registrosForm.map((reg, idx) => (
                          <TableRow key={reg.trampa_id}>
                            <TableCell className="font-medium">#{reg.numero_trampa}</TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="0"
                                value={reg.hembras}
                                onChange={(e) => updateRegistroForm(idx, 'hembras', parseInt(e.target.value) || 0)}
                                className="w-20 h-8"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="0"
                                value={reg.machos}
                                onChange={(e) => updateRegistroForm(idx, 'machos', parseInt(e.target.value) || 0)}
                                className="w-20 h-8"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="0"
                                value={reg.sin_clasificar}
                                onChange={(e) => updateRegistroForm(idx, 'sin_clasificar', parseInt(e.target.value) || 0)}
                                className="w-20 h-8"
                              />
                            </TableCell>
                            <TableCell className={`font-bold ${
                              (reg.hembras + reg.machos + reg.sin_clasificar) > 5 ? 'text-red-600' : 'text-emerald-600'
                            }`}>
                              {reg.hembras + reg.machos + reg.sin_clasificar}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Tabla de mantenimiento */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-orange-50 dark:bg-orange-900/20 px-4 py-2 font-semibold text-orange-800 dark:text-orange-200">
                    Mantenimiento (¿Se cambió?) y Evidencia
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-20">#Trampa</TableHead>
                          <TableHead className="text-center">Miel</TableHead>
                          <TableHead className="text-center">Feromonas</TableHead>
                          <TableHead className="text-center">Galón</TableHead>
                          <TableHead className="text-center">Tapa</TableHead>
                          <TableHead className="text-center">Alambres</TableHead>
                          <TableHead className="text-center">Stickers</TableHead>
                          <TableHead>Detalle</TableHead>
                          <TableHead className="text-center">Foto</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {registrosForm.map((reg, idx) => (
                          <TableRow key={`mant-${reg.trampa_id}`}>
                            <TableCell className="font-medium">#{reg.numero_trampa}</TableCell>
                            <TableCell className="text-center">
                              <Checkbox
                                checked={reg.cambio_miel_caldo}
                                onCheckedChange={(checked) => updateRegistroForm(idx, 'cambio_miel_caldo', checked)}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Checkbox
                                checked={reg.cambio_feromonas}
                                onCheckedChange={(checked) => updateRegistroForm(idx, 'cambio_feromonas', checked)}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Checkbox
                                checked={reg.cambio_galon}
                                onCheckedChange={(checked) => updateRegistroForm(idx, 'cambio_galon', checked)}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Checkbox
                                checked={reg.cambio_tapa}
                                onCheckedChange={(checked) => updateRegistroForm(idx, 'cambio_tapa', checked)}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Checkbox
                                checked={reg.cambio_alambres}
                                onCheckedChange={(checked) => updateRegistroForm(idx, 'cambio_alambres', checked)}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Checkbox
                                checked={reg.cambio_stickers}
                                onCheckedChange={(checked) => updateRegistroForm(idx, 'cambio_stickers', checked)}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={reg.detalle || ''}
                                onChange={(e) => updateRegistroForm(idx, 'detalle', e.target.value)}
                                placeholder="Observaciones..."
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              {reg.imagen_evidencia ? (
                                <div className="flex items-center gap-1 justify-center">
                                  <Badge variant="outline" className="text-xs text-emerald-600">
                                    <Image className="w-3 h-3 mr-1" />
                                    Subida
                                  </Badge>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => updateRegistroForm(idx, 'imagen_evidencia', null)}
                                    className="h-6 w-6 p-0 text-red-500"
                                    title="Eliminar imagen"
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="relative">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleUploadImage(idx, e.target.files[0])}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    disabled={uploadingImage[idx]}
                                  />
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 text-xs"
                                    disabled={uploadingImage[idx]}
                                  >
                                    {uploadingImage[idx] ? (
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-emerald-600" />
                                    ) : (
                                      <>
                                        <Camera className="w-3 h-3 mr-1" />
                                        Subir
                                      </>
                                    )}
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
              {currentStep > 1 && (
                <Button variant="outline" onClick={() => setCurrentStep(currentStep - 1)}>
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Anterior
                </Button>
              )}
              {currentStep < 2 ? (
                <Button 
                  onClick={() => setCurrentStep(2)}
                  disabled={!formData.fecha || !formData.lote || !formData.encargado_id}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  Siguiente
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleGuardar} className="bg-emerald-600 hover:bg-emerald-700">
                  <Check className="w-4 h-4 mr-1" />
                  Guardar Registros
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de edición */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5 text-amber-600" />
                Editar Registro - Trampa #{registroEditar?.numero_trampa}
              </DialogTitle>
            </DialogHeader>
            
            {registroEditar && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Hembras (H)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={registroEditar.hembras}
                      onChange={(e) => setRegistroEditar({ 
                        ...registroEditar, 
                        hembras: parseInt(e.target.value) || 0 
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Machos (M)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={registroEditar.machos}
                      onChange={(e) => setRegistroEditar({ 
                        ...registroEditar, 
                        machos: parseInt(e.target.value) || 0 
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sin Clasificar (SC)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={registroEditar.sin_clasificar}
                      onChange={(e) => setRegistroEditar({ 
                        ...registroEditar, 
                        sin_clasificar: parseInt(e.target.value) || 0 
                      })}
                    />
                  </div>
                </div>

                <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  <Label className="mb-2 block">Mantenimiento (¿Se cambió?)</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { key: 'cambio_miel_caldo', label: 'Miel/Caldo' },
                      { key: 'cambio_feromonas', label: 'Feromonas' },
                      { key: 'cambio_galon', label: 'Galón' },
                      { key: 'cambio_tapa', label: 'Tapa' },
                      { key: 'cambio_alambres', label: 'Alambres' },
                      { key: 'cambio_stickers', label: 'Stickers' },
                    ].map(({ key, label }) => (
                      <div key={key} className="flex items-center gap-2">
                        <Checkbox
                          checked={registroEditar[key]}
                          onCheckedChange={(checked) => setRegistroEditar({
                            ...registroEditar,
                            [key]: checked
                          })}
                        />
                        <span className="text-sm">{label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Detalle / Observaciones</Label>
                  <Input
                    value={registroEditar.detalle || ''}
                    onChange={(e) => setRegistroEditar({ 
                      ...registroEditar, 
                      detalle: e.target.value 
                    })}
                    placeholder="Observaciones adicionales..."
                  />
                </div>

                <div className="space-y-2">
                  <Label>Encargado</Label>
                  <Select 
                    value={registroEditar.encargado_id} 
                    onValueChange={(id) => {
                      const trabajador = trabajadores.find(t => t.id === id);
                      setRegistroEditar({ 
                        ...registroEditar, 
                        encargado_id: id,
                        encargado_nombre: trabajador?.nombre_completo || ''
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {trabajadores.map(t => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.nombre_completo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveEdit} 
                disabled={savingEdit}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {savingEdit ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-1" />
                )}
                Guardar Cambios
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de historial */}
        <Dialog open={showHistorialModal} onOpenChange={setShowHistorialModal}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-blue-600" />
                Historial de Cambios - Trampa #{historialData?.trampa_numero}
              </DialogTitle>
            </DialogHeader>
            
            {loadingHistorial ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
              </div>
            ) : historialData ? (
              <div className="space-y-4 py-4">
                <div className="flex gap-4 text-sm text-gray-600 dark:text-gray-400">
                  <span><strong>Lote:</strong> {historialData.lote}</span>
                  <span><strong>Fecha registro:</strong> {historialData.fecha}</span>
                </div>

                {historialData.historial?.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No hay cambios registrados</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {historialData.historial?.map((entrada, idx) => (
                      <div 
                        key={entrada.id || idx}
                        className={`p-3 rounded-lg border ${
                          entrada.accion === 'creacion' 
                            ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20' 
                            : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <Badge variant={entrada.accion === 'creacion' ? 'default' : 'secondary'}>
                            {entrada.accion === 'creacion' ? '✨ Creación' : '✏️ Modificación'}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {new Date(entrada.fecha).toLocaleString('es')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          <strong>Usuario:</strong> {entrada.usuario}
                        </p>
                        {entrada.cambios?.length > 0 && (
                          <div className="space-y-1">
                            {entrada.cambios.map((cambio, cIdx) => (
                              <div key={cIdx} className="text-sm flex gap-2">
                                <span className="font-medium text-gray-700 dark:text-gray-300">
                                  {cambio.campo}:
                                </span>
                                <span className="text-red-500 line-through">
                                  {String(cambio.valor_anterior)}
                                </span>
                                <span>→</span>
                                <span className="text-emerald-600 font-medium">
                                  {String(cambio.valor_nuevo)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowHistorialModal(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de imagen */}
        <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Image className="w-5 h-5 text-blue-600" />
                Evidencia Fotográfica
              </DialogTitle>
            </DialogHeader>
            
            {imagenSeleccionada && (
              <div className="flex justify-center py-4">
                <img 
                  src={`${BACKEND_URL}/api/drive/image-proxy/${imagenSeleccionada.split('/').pop()}`}
                  alt="Evidencia de trampa"
                  className="max-h-[60vh] rounded-lg shadow-lg"
                  onError={(e) => {
                    e.target.src = imagenSeleccionada;
                  }}
                />
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowImageModal(false)}>
                Cerrar
              </Button>
              {imagenSeleccionada && (
                <Button 
                  onClick={() => window.open(imagenSeleccionada, '_blank')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Abrir en nueva pestaña
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de confirmación de eliminación */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Confirmar Eliminación
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-gray-600">
                ¿Está seguro que desea eliminar el registro de la <strong>Trampa #{registroEliminar?.numero_trampa}</strong> del <strong>{registroEliminar?.fecha}</strong>?
              </p>
              <p className="text-sm text-red-500 mt-2">
                Esta acción no se puede deshacer.
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setRegistroEliminar(null);
                }}
                disabled={deletingRegistro}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deletingRegistro}
              >
                {deletingRegistro ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ProduccionLayout>
  );
}
